import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import LandingShell from '@/components/landing/LandingShell';
import SEO from '@/components/common/SEO';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useToast } from '@/hooks/use-toast';
import { useForum } from '@/hooks/useForum';
import { useAuth } from '@/contexts/AuthContext';
import { useRouteSsrData, useSsrPayload } from '@/lib/ssr/context';
import type { ForumsPageData } from '@/types/ssr';
import { MessageSquare, Heart, Search, ArrowLeft, Share2 } from 'lucide-react';
import { getForumsSeo } from '@/lib/seo/publicMetadata';
import { formatForumPostContent } from '@/utils/htmlUtils';
import { formatRelativeSsrDate } from '@/lib/ssr/date';

export default function Community() {
  const initialData = useRouteSsrData<ForumsPageData>('forums');
  const ssrPayload = useSsrPayload();
  const {
    categories,
    threads,
    posts,
    loading,
    fetchPosts,
    addThread,
    addReply,
    toggleLike,
    fetchLikes,
    fetchUserLiked,
    incrementThreadViews,
  } = useForum({
    categories: initialData?.categories || [],
    threads: initialData?.threads || [],
    posts: initialData?.posts || [],
  });

  const { user } = useAuth();
  const { toast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();

  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(() => initialData?.selectedThreadId || null);
  const [replyText, setReplyText] = useState('');
  const [newThread, setNewThread] = useState({ title: '', content: '', category: '' });
  const [showNewThread, setShowNewThread] = useState(false);
  const [likeState, setLikeState] = useState<Record<string, boolean>>({});
  const [likeCounts, setLikeCounts] = useState<Record<string, number>>({});

  const formatDate = (dateString: string) => formatRelativeSsrDate(dateString, ssrPayload?.generatedAt);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const threadId = params.get('thread');
    if (threadId) {
      setSelectedThreadId(threadId);
    }
  }, [location.search]);

  useEffect(() => {
    if (!selectedThreadId) {
      return;
    }

    const params = new URLSearchParams(location.search);
    if (params.get('thread') !== selectedThreadId) {
      params.set('thread', selectedThreadId);
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    }
  }, [location.pathname, location.search, navigate, selectedThreadId]);

  useEffect(() => {
    if (!selectedThreadId) {
      return;
    }

    void fetchPosts(selectedThreadId);
    void incrementThreadViews(selectedThreadId);
  }, [fetchPosts, incrementThreadViews, selectedThreadId]);

  useEffect(() => {
    if (posts.length === 0) {
      setLikeCounts({});
      setLikeState({});
      return;
    }

    void Promise.all(
      posts.map(async (post) => {
        const [likes, liked] = await Promise.all([fetchLikes(post.id), fetchUserLiked(post.id)]);
        return { id: post.id, likes, liked };
      })
    ).then((results) => {
      setLikeCounts(
        results.reduce<Record<string, number>>((accumulator, result) => {
          accumulator[result.id] = result.likes;
          return accumulator;
        }, {})
      );
      setLikeState(
        results.reduce<Record<string, boolean>>((accumulator, result) => {
          accumulator[result.id] = result.liked;
          return accumulator;
        }, {})
      );
    });
  }, [fetchLikes, fetchUserLiked, posts]);

  const filteredThreads = useMemo(
    () =>
      threads.filter(
        (thread) =>
          (selectedCategory === 'all' || thread.category_id === selectedCategory) &&
          (!searchQuery || thread.title.toLowerCase().includes(searchQuery.toLowerCase()))
      ),
    [searchQuery, selectedCategory, threads]
  );

  const currentThread = useMemo(
    () => threads.find((thread) => thread.id === selectedThreadId) || null,
    [selectedThreadId, threads]
  );

  const currentThreadFirstPost = posts[0];

  const handleCreateThread = async () => {
    if (!newThread.title.trim() || !newThread.content.trim() || !newThread.category) {
      toast({ title: 'Missing info', description: 'Please fill in all fields.', variant: 'destructive' });
      return;
    }

    const threadId = await addThread(newThread.title.trim(), newThread.category, newThread.content.trim());
    if (!threadId) {
      return;
    }

    setNewThread({ title: '', content: '', category: '' });
    setShowNewThread(false);
    setSelectedThreadId(threadId);
    toast({ title: 'Thread posted' });
  };

  const handleReply = async () => {
    if (!selectedThreadId || !replyText.trim()) {
      return;
    }

    await addReply(selectedThreadId, replyText.trim());
    setReplyText('');
    toast({ title: 'Reply posted' });
  };

  const handleBackToThreads = () => {
    setSelectedThreadId(null);
    const params = new URLSearchParams(location.search);
    params.delete('thread');
    navigate(`${location.pathname}${params.toString() ? `?${params.toString()}` : ''}`, { replace: true });
  };

  const handleShareThread = async () => {
    if (!currentThread) {
      return;
    }

    const shareUrl = `${window.location.origin}/forums?thread=${currentThread.id}`;
    await navigator.clipboard.writeText(shareUrl);
    toast({ title: 'Thread link copied' });
  };

  const handleToggleLike = async (postId: string) => {
    const liked = likeState[postId] || false;
    await toggleLike(postId, liked);
    setLikeState((current) => ({ ...current, [postId]: !liked }));
    setLikeCounts((current) => ({ ...current, [postId]: (current[postId] || 0) + (liked ? -1 : 1) }));
  };

  const seo = getForumsSeo({
    threadId: selectedThreadId,
    threadTitle: currentThread?.title,
    firstPostContent: currentThreadFirstPost?.content,
  });

  return (
    <LandingShell>
      <SEO {...seo} />

      <div className="container mx-auto py-8 px-4">
        <div className="max-w-6xl mx-auto space-y-8">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2">MapleOS Forums</h1>
              <p className="text-muted-foreground max-w-3xl">
                Join discussions with fellow MapleOS users and the core team. Share experiences, get help, and discover new ways to use the platform.
              </p>
            </div>
            <Button onClick={() => (user ? setShowNewThread((current) => !current) : navigate('/auth'))}>
              <MessageSquare className="mr-2 h-4 w-4" />
              {showNewThread ? 'Close composer' : 'Start a thread'}
            </Button>
          </div>

          {showNewThread && (
            <Card>
              <CardHeader>
                <CardTitle>New thread</CardTitle>
                <CardDescription>Create a new discussion in the MapleOS forums.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Input
                  placeholder="Thread title"
                  value={newThread.title}
                  onChange={(event) => setNewThread((current) => ({ ...current, title: event.target.value }))}
                />
                <Tabs value={newThread.category || 'unselected'} onValueChange={(value) => value !== 'unselected' && setNewThread((current) => ({ ...current, category: value }))}>
                  <TabsList className="flex h-auto flex-wrap justify-start gap-2 bg-transparent p-0">
                    {categories.map((category) => (
                      <TabsTrigger key={category.id} value={category.id} className="rounded-full border px-4 py-2 data-[state=active]:border-primary data-[state=active]:bg-primary/10">
                        {category.title}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </Tabs>
                <Textarea
                  placeholder="Share your question, idea, or feedback"
                  value={newThread.content}
                  onChange={(event) => setNewThread((current) => ({ ...current, content: event.target.value }))}
                  className="min-h-40"
                />
                <div className="flex justify-end">
                  <Button onClick={() => void handleCreateThread()}>Post thread</Button>
                </div>
              </CardContent>
            </Card>
          )}

          {selectedThreadId && currentThread ? (
            <div className="space-y-6">
              <div className="flex items-center justify-between gap-4">
                <Button variant="ghost" onClick={handleBackToThreads}>
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to threads
                </Button>
                <Button variant="outline" onClick={() => void handleShareThread()}>
                  <Share2 className="mr-2 h-4 w-4" />
                  Share thread
                </Button>
              </div>

              <Card>
                <CardHeader>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge>{categories.find((category) => category.id === currentThread.category_id)?.title || 'Discussion'}</Badge>
                    <Badge variant="outline">{currentThread.replies || Math.max(posts.length - 1, 0)} replies</Badge>
                  </div>
                  <CardTitle className="text-3xl">{currentThread.title}</CardTitle>
                  <CardDescription>Started {formatDate(currentThread.created_at)}</CardDescription>
                </CardHeader>
              </Card>

              <div className="space-y-4">
                {posts.map((post, index) => (
                  <Card key={post.id}>
                    <CardContent className="pt-6 space-y-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-center gap-3">
                          <Avatar>
                            <AvatarImage src={post.user?.avatar || undefined} />
                            <AvatarFallback>{post.user?.name?.slice(0, 2).toUpperCase() || 'US'}</AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="font-medium">{post.user?.name || 'User'}</div>
                            <div className="text-xs text-muted-foreground">
                              {index === 0 ? 'Original post' : 'Reply'} · {formatDate(post.created_at)}
                            </div>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => void handleToggleLike(post.id)}>
                          <Heart className={`mr-2 h-4 w-4 ${likeState[post.id] ? 'fill-current text-primary' : ''}`} />
                          {likeCounts[post.id] || 0}
                        </Button>
                      </div>
                      <div
                        className="prose prose-sm max-w-none break-words text-foreground dark:prose-invert prose-p:my-3 prose-ul:my-3 prose-ol:my-3 prose-li:my-1 prose-pre:overflow-x-auto prose-a:text-primary prose-a:no-underline hover:prose-a:underline"
                        dangerouslySetInnerHTML={{ __html: formatForumPostContent(post.content) }}
                      />
                    </CardContent>
                  </Card>
                ))}
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>Reply</CardTitle>
                  <CardDescription>Add your response to this thread.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Textarea
                    placeholder={user ? 'Write your reply' : 'Sign in to reply'}
                    value={replyText}
                    onChange={(event) => setReplyText(event.target.value)}
                    className="min-h-32"
                    disabled={!user}
                  />
                  <div className="flex justify-end">
                    <Button onClick={() => void handleReply()} disabled={!user || !replyText.trim()}>
                      Post reply
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="flex flex-col gap-4 rounded-2xl border bg-card p-4 md:flex-row md:items-center md:justify-between">
                <div className="flex items-center gap-3 rounded-xl border bg-background px-3 py-2 md:w-[22rem]">
                  <Search className="h-4 w-4 text-muted-foreground" />
                  <input
                    className="w-full bg-transparent text-sm outline-none"
                    placeholder="Search threads"
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                  />
                </div>
                <Tabs value={selectedCategory} onValueChange={setSelectedCategory}>
                  <TabsList className="flex h-auto flex-wrap justify-start gap-2 bg-transparent p-0">
                    <TabsTrigger value="all" className="rounded-full border px-4 py-2 data-[state=active]:border-primary data-[state=active]:bg-primary/10">
                      All Topics
                    </TabsTrigger>
                    {categories.map((category) => (
                      <TabsTrigger
                        key={category.id}
                        value={category.id}
                        className="rounded-full border px-4 py-2 data-[state=active]:border-primary data-[state=active]:bg-primary/10"
                      >
                        {category.title}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </Tabs>
              </div>

              {loading ? (
                <Card>
                  <CardContent className="pt-6 text-muted-foreground">Loading forum…</CardContent>
                </Card>
              ) : filteredThreads.length === 0 ? (
                <Card>
                  <CardContent className="pt-6 text-muted-foreground">No threads match this filter yet.</CardContent>
                </Card>
              ) : (
                <div className="grid gap-4">
                  {filteredThreads.map((thread) => (
                    <Card key={thread.id} className="transition-colors hover:border-primary/30">
                      <CardContent className="pt-6">
                        <button className="w-full text-left" onClick={() => setSelectedThreadId(thread.id)}>
                          <div className="flex flex-wrap items-center gap-2">
                            <Badge variant={thread.is_pinned ? 'default' : 'outline'}>
                              {categories.find((category) => category.id === thread.category_id)?.title || 'Topic'}
                            </Badge>
                            {thread.is_pinned && <Badge>Featured</Badge>}
                          </div>
                          <h2 className="mt-3 text-xl font-semibold">{thread.title}</h2>
                          <p className="mt-2 text-sm text-muted-foreground">
                            {thread.user?.name || 'User'} · {formatDate(thread.updated_at)} · {thread.replies || 0} replies · {thread.views_count || 0} views
                          </p>
                        </button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </LandingShell>
  );
}